import { Fragment } from "react";

import { FieldRequestFieldEnum } from "@/features/common/enums";
import type { IFieldRequest } from "@/features/lender-responses/types/field-request";
import type { IFieldRequestInput } from "@/features/lender-responses/types/lender-response";
import { ucFirst } from "@/utils";
import { FIELD_REQUESTS_LABELS } from "@/utils/fieldRequests/fieldRequests";

const getNumberOfGuarantorsText = (numberOfGuarantors: number) => {
  if (numberOfGuarantors > 1) {
    return `${numberOfGuarantors} guarantors required`;
  }
  if (numberOfGuarantors === 1) {
    return "1 guarantor required";
  }

  return null;
};

const getReducedGuarantorPercentageText = (
  reducedGuarantorPercentTo: number,
) => {
  if (reducedGuarantorPercentTo < 100) {
    return `${reducedGuarantorPercentTo}% guarantee requirement`;
  }

  return null;
};

const LenderResponseFieldRequestDetails = ({
  title,
  fieldRequests,
}: {
  title: string;
  fieldRequests: (IFieldRequest | IFieldRequestInput)[];
}) => {
  const renderField = ({
    config,
    field,
  }: IFieldRequestInput | IFieldRequest) => {
    const { numberOfRequiredGuarantors = 0, reducedGuarantorPercentTo = 100 } =
      config || {};

    if (field === FieldRequestFieldEnum.guarantors) {
      const text = "Guarantor";

      const numberOfGuarantorsText = getNumberOfGuarantorsText(
        numberOfRequiredGuarantors || 0,
      );
      const reducedGuarantorPercentageText = getReducedGuarantorPercentageText(
        reducedGuarantorPercentTo || 100,
      );

      if (numberOfGuarantorsText && reducedGuarantorPercentageText) {
        return `${text} (${reducedGuarantorPercentageText}, ${numberOfGuarantorsText})`;
      }
      if (numberOfGuarantorsText) {
        return `${text} (${numberOfGuarantorsText})`;
      }
      if (reducedGuarantorPercentageText) {
        return `${text} (${reducedGuarantorPercentageText})`;
      }

      return text;
    }

    return (
      FIELD_REQUESTS_LABELS[field as keyof typeof FIELD_REQUESTS_LABELS] ||
      ucFirst(field.replace(/_+/g, " "))
    );
  };

  return (
    <Fragment>
      <h5 className="mb-3">{title}</h5>
      <ul className="mb-6" style={{ paddingInlineStart: "20px" }}>
        {Array.isArray(fieldRequests) &&
          fieldRequests.map((item, index) => (
            <li key={"id" in item ? item.id : index} className="mb-3">
              <div className="flex">
                <div className="grow">
                  <div>{renderField(item)}</div>

                  {item.lenderComment && (
                    <div className="ml-5 mt-1 text-sm">
                      {item.lenderComment}
                    </div>
                  )}
                </div>
              </div>
            </li>
          ))}
      </ul>
    </Fragment>
  );
};

export default LenderResponseFieldRequestDetails;
